import { useEffect, useRef, useState } from "react";

import AddressFormatter from "@shopify/address";
import { injectIntl, type intlFormatPropTypes } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { Spinner } from "shared-components/decorators/spinner";
import SharedIntlProviderHOC from "shared-components/hocs/sharedIntlProviderHOC";

import { hot } from "hot";

import { AddSpaceAddressForm } from "../../add-a-space/AddSpaceAddressForm";
import type { Country } from "../../AddressForm/AddressForm.types";
import { fetchEnvWeb } from "../actions/env";
import { fetchBillingAddress } from "../actions/fee-profile";
import { setNavStep } from "../actions/nav";
import { fetchSpace, fetchSpaceCountryStatus } from "../actions/space";
import {
  selectBillingAddressHasFetched,
  selectHasBillingAddress,
} from "../selectors/fee-profile.selectors";
import {
  selectProfileHasFetched,
  selectProfileIsFetching,
  selectProfileSSOid,
} from "../selectors/profile.selectors";
import {
  selectSpaceCompletionPercentage,
  selectSpaceCountryStatusHasFetched,
  selectSpaceHasFetched,
  selectSpaceId,
  selectSpaceIsFetching,
  selectSpaceOwnerId,
  selectSpaceStatus,
} from "../selectors/space.selectors";

const DEFAULT_COMPLETION_PERCENTAGE = 5;

type AddASpaceAddressContainerProps = {
  accessToken: string;
  intl: intlFormatPropTypes;
  params: { spaceId?: string };
};

const usePrevious = <T,>(value: T) => {
  const ref = useRef(value);
  const previous = ref.current;

  useEffect(() => {
    ref.current = value;
  });

  return previous;
};

const AddASpaceAddressContainer = ({
  accessToken,
  intl,
  params: { spaceId },
}: AddASpaceAddressContainerProps) => {
  const dispatch = useDispatch();

  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [globalCountries, setGlobalCountries] = useState<Country[]>([]);
  const [completionPercentage, setCompletionPercentage] = useState(
    DEFAULT_COMPLETION_PERCENTAGE,
  );

  const ssoId = useSelector(selectProfileSSOid);
  const profileIsFetching = useSelector(selectProfileIsFetching);
  const profileHasFetched = useSelector(selectProfileHasFetched);
  const spaceIsFetching = useSelector(selectSpaceIsFetching);
  const spaceHasFetched = useSelector(selectSpaceHasFetched);
  const spaceOwnerId = useSelector(selectSpaceOwnerId);
  const spaceStatus = useSelector(selectSpaceStatus);
  const fetchedSpaceId = useSelector(selectSpaceId);
  const fetchedCompletionPercentage = useSelector(
    selectSpaceCompletionPercentage,
  );
  const countryStatusHasFetched = useSelector(
    selectSpaceCountryStatusHasFetched,
  );
  const billingAddressHasFetched = useSelector(selectBillingAddressHasFetched);
  const hasBillingAddress = useSelector(selectHasBillingAddress);

  const wasProfileFetching = usePrevious(profileIsFetching);
  const wasSpaceFetching = usePrevious(spaceIsFetching);

  const isEdit = !!spaceId && "DRAFT" !== spaceStatus;

  useEffect(() => {
    const locale = SharedIntlProviderHOC.getLocale().split("-")[0];

    new AddressFormatter(locale).getCountries().then(setAllCountries);
    new AddressFormatter("en").getCountries().then(setGlobalCountries);

    dispatch(fetchEnvWeb());

    if (spaceId) {
      dispatch(fetchSpace(spaceId, accessToken));
    }
    // Mount only: the route's spaceId does not change without remounting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (wasProfileFetching && !profileIsFetching) {
      dispatch(fetchSpaceCountryStatus(ssoId ?? "", accessToken));
      dispatch(fetchBillingAddress(accessToken, ssoId ?? ""));
    }
  }, [accessToken, dispatch, profileIsFetching, ssoId, wasProfileFetching]);

  useEffect(() => {
    if (wasSpaceFetching && !spaceIsFetching) {
      setCompletionPercentage(
        fetchedCompletionPercentage ?? DEFAULT_COMPLETION_PERCENTAGE,
      );
    }
  }, [fetchedCompletionPercentage, spaceIsFetching, wasSpaceFetching]);

  useEffect(() => {
    if (profileHasFetched && spaceHasFetched && ssoId !== spaceOwnerId) {
      window.location.href = "/host";
    }
  }, [profileHasFetched, spaceHasFetched, spaceOwnerId, ssoId]);

  useEffect(() => {
    if (!isEdit) {
      dispatch(
        setNavStep(
          intl.formatMessage({
            id: "hosting.add_a_space_address_container.address",
            defaultMessage: "Address",
          }),
          completionPercentage,
        ),
      );
    }
  }, [completionPercentage, dispatch, intl, isEdit]);

  // A latch: once the page has rendered, a flag flipping back mid-submit must not
  // drop the host back to a spinner and unmount the form they are filling in.
  const hasLoaded = useRef(false);

  if (
    profileHasFetched &&
    countryStatusHasFetched &&
    (!spaceId || spaceHasFetched) &&
    billingAddressHasFetched
  ) {
    hasLoaded.current = true;
  }

  if (!hasLoaded.current) {
    return (
      <div>
        <div className="spinner-container">
          <Spinner additionalStyle="gray" />
        </div>
      </div>
    );
  }

  return (
    <div className="AddASpaceOpHoursContainer ps-container-lg-margin ps-container-fixed">
      <div className="ps-row">
        <div className="col-xs-12">
          <AddSpaceAddressForm
            accessToken={accessToken}
            spaceId={fetchedSpaceId}
            isEdit={isEdit}
            hasBillingAddress={hasBillingAddress}
            allCountries={allCountries}
            globalCountries={globalCountries}
          />
        </div>
      </div>
    </div>
  );
};

export default hot()(injectIntl(AddASpaceAddressContainer));
